import type { ParamSpec } from "@cfg/design-sdk";

/** Longest generated name (the API allows 120 characters). */
export const MAX_NAME = 120;

/** `armLength` -> ["arm", "Length"], `wheel_diameter` -> ["wheel", "diameter"]. */
function words(name: string): string[] {
  return name
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .split(/[^a-z0-9]+/i)
    .filter(Boolean);
}

/**
 * A short key per param, unique within the spec: its acronym, lengthened with the following
 * letters of its last word when an earlier param already took it (`width` "W", `wheelbase` "WH").
 */
function shortKeys(spec: ParamSpec[]): Map<string, string> {
  const keys = new Map<string, string>();
  const taken = new Set<string>();
  for (const s of spec) {
    const w = words(s.name);
    // The initials of the param's name, in capitals: `armLength` "AL", `wheel_diameter` "WD".
    const base = w.map((x) => x[0]!.toUpperCase()).join("") || s.name.toUpperCase();
    const tail = (w.at(-1) ?? "").slice(1).toUpperCase();
    let key = base;
    for (let i = 0; taken.has(key) && i < tail.length; i++) key = base + tail.slice(0, i + 1);
    for (let n = 2; taken.has(key); n++) key = `${base}${n}`;
    taken.add(key);
    keys.set(s.name, key);
  }
  return keys;
}

function formatValue(v: unknown): string {
  if (typeof v === "number") return String(Math.round(v * 1000) / 1000);
  return String(v ?? "");
}

/**
 * Every param as its short key and value, in the design's order, the way the parts engrave
 * them: "WD43.5 TA20 W33 S:ribbed N M". A switched-on option reads as its key alone and a
 * switched-off one is left out.
 */
export function paramSummary(spec: ParamSpec[], params: Record<string, unknown>, defaults: Record<string, unknown>): string {
  const keys = shortKeys(spec);
  const parts: string[] = [];
  for (const s of spec) {
    const v = s.name in params ? params[s.name] : defaults[s.name];
    if (v === undefined) continue;
    const key = keys.get(s.name)!;
    if (typeof v === "boolean") {
      if (v) parts.push(key);
    } else if (s.type === "select") parts.push(`${key}:${formatValue(v)}`);
    else parts.push(`${key}${formatValue(v)}`);
  }
  return parts.join(" ");
}

/** The name a saved configuration gets: the design's title and its parameter summary, cut to `MAX_NAME`. */
export function configName(title: string, spec: ParamSpec[], params: Record<string, unknown>, defaults: Record<string, unknown>): string {
  const summary = paramSummary(spec, params, defaults);
  const full = summary ? `${title} · ${summary}` : title;
  return full.length <= MAX_NAME ? full : `${full.slice(0, MAX_NAME - 1).trimEnd()}…`;
}

/** Same printable part: equal once the defaults are filled in (key order and spelled-out defaults don't matter). */
export function sameParams(a: Record<string, unknown>, b: Record<string, unknown>, defaults: Record<string, unknown>): boolean {
  const full = (p: Record<string, unknown>) => ({ ...defaults, ...p });
  const x = full(a);
  const y = full(b);
  const keys = new Set([...Object.keys(x), ...Object.keys(y)]);
  for (const k of keys) if (JSON.stringify(x[k]) !== JSON.stringify(y[k])) return false;
  return true;
}

/** The bits of a design's `ui` the readable summary looks at. */
export interface SummaryUi {
  groups?: { params: string[]; collapsed?: boolean }[];
}

function readableValue(s: ParamSpec, v: unknown): string {
  if (s.type === "select") return s.options?.find((o) => o.value === String(v))?.label ?? String(v);
  const n = formatValue(v);
  if (!s.unit) return n;
  return s.unit === "°" ? `${n}°` : `${n} ${s.unit}`;
}

/**
 * The params as people read them, in the design's order and in the spec's (translated) words:
 * ["Wheel diameter 50 mm", "Wheel thickness 12 mm", "Wheel separation 4 studs", "Nameplate"].
 * Leaves out advanced params and params in collapsed groups (printer-fit tweaks). An option reads
 * as its label when switched on and off by default ("Nameplate"); options that are on by default
 * (the brand mark) or switched off are left out, the code keeps them.
 */
export function readableParts(spec: ParamSpec[], params: Record<string, unknown>, defaults: Record<string, unknown>, ui?: SummaryUi): string[] {
  const hidden = new Set((ui?.groups ?? []).filter((g) => g.collapsed).flatMap((g) => g.params));
  const parts: string[] = [];
  for (const s of spec) {
    if (s.advanced || hidden.has(s.name)) continue;
    const v = s.name in params ? params[s.name] : defaults[s.name];
    if (v === undefined) continue;
    if (typeof v === "boolean") {
      if (v && s.default !== true) parts.push(s.label);
    } else parts.push(`${s.label} ${readableValue(s, v)}`);
  }
  return parts;
}

/** `readableParts` joined: "Wheel diameter 50 mm · Wheel thickness 12 mm · Wheel separation 4 studs". */
export function readableSummary(spec: ParamSpec[], params: Record<string, unknown>, defaults: Record<string, unknown>, ui?: SummaryUi): string {
  return readableParts(spec, params, defaults, ui).join(" · ");
}

/**
 * Whether a saved name is the generated one ("<title> · <code>", possibly cut with "…") for these
 * params, in whatever language the title was in, rather than a name the user typed.
 */
export function isAutoName(name: string, spec: ParamSpec[], params: Record<string, unknown>, defaults: Record<string, unknown>): boolean {
  const code = paramSummary(spec, params, defaults);
  const at = name.lastIndexOf(" · ");
  if (at < 0) return !code && !!name;
  const rest = name.slice(at + 3);
  if (rest === code) return true;
  return rest.endsWith("…") && code.startsWith(rest.slice(0, -1).trimEnd());
}
